import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Section, Empty, StatusBadge } from "@/components/kit";
import useLoad from "@/lib/useLoad";
import { useAuth, isAdmin } from "@/context/AuthContext";
import api, { errMsg, fmtDate } from "@/lib/api";

const in7 = () => new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
const LOGIN = { active: ["Signed in", "text-emerald-400"], invited: ["Invited", "text-amber-400"], none: ["No login", "text-slate-500"] };

export default function Clients() {
  const [params, setParams] = useSearchParams();
  const [clients, reload] = useLoad("/clients");
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [coaches] = useLoad(admin ? "/admin/coaches" : null);
  const [packages] = useLoad("/billing/packages");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendInvite, setSendInvite] = useState(true);
  const [sendWelcome, setSendWelcome] = useState(true);
  const [member, setMember] = useState({ name: "", email: "", phone: "", assigned_coach_id: "", package_id: "none" });
  const status = params.get("status") || "all";
  const coach = params.get("coach") || "all";
  const setF = (k, v) => { const p = new URLSearchParams(params); v === "all" ? p.delete(k) : p.set(k, v); setParams(p); };
  const coachFilters = [...new Map((clients || []).filter((c) => c.coach_id).map((c) => [c.coach_id, c.coach_name])).entries()];
  const list = (clients || []).filter((c) => `${c.name} ${c.email}`.toLowerCase().includes(q.toLowerCase()))
    .filter((c) => (status === "all" ? true : status === "ending" ? c.status === "active" && c.end_date && c.end_date <= in7() : c.status === status))
    .filter((c) => (coach === "all" ? true : coach === "none" ? !c.coach_id : c.coach_id === coach));
  const addMember = async (event) => {
    event.preventDefault();
    const [first_name, ...lastParts] = member.name.trim().split(/\s+/);
    setSaving(true);
    try {
      const { data } = await api.post("/clients", {
        first_name, last_name: lastParts.join(" "), email: member.email.trim(), phone: member.phone.trim(),
        assigned_coach_id: admin ? member.assigned_coach_id : null,
        package_id: member.package_id === "none" ? null : member.package_id,
        send_invite: sendInvite, send_welcome: sendWelcome,
      });
      setMember({ name: "", email: "", phone: "", assigned_coach_id: "", package_id: "none" });
      setShowAdd(false);
      reload();
      const notices = [];
      if (data.invite.status === "sent") notices.push("sign-in code sent");
      else if (data.invite.status === "failed") notices.push(`sign-in code failed: ${data.invite.message}`);
      if (data.welcome_email.status === "failed") notices.push(`welcome email failed: ${data.welcome_email.message}`);
      if (notices.some((notice) => notice.includes("failed"))) toast.warning(`Member added; ${notices.join("; ")}`);
      else toast.success(notices.length ? `Member added; ${notices.join(" and ")}` : "Member added");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="CRM members" title="Clients" testid="clients-title">
        <Button type="button" onClick={() => setShowAdd((open) => !open)} data-testid="clients-add-member-btn">
          <UserPlus size={15} className="mr-1" />Add member
        </Button>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="pl-9 w-56" data-testid="clients-search-input" />
        </div>
        <Select value={coach} onValueChange={(v) => setF("coach", v)}>
          <SelectTrigger className="w-44" data-testid="clients-coach-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All coaches</SelectItem><SelectItem value="none">Unassigned</SelectItem>
            {coachFilters.map(([id, name]) => <SelectItem key={id} value={id}>{name || "Coach"}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>
      {showAdd && (
        <Section title="Add member" testid="clients-add-member-form">
          <form onSubmit={addMember} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <div className="space-y-1">
                <Label htmlFor="new-member-name">Full name</Label>
                <Input id="new-member-name" required value={member.name} onChange={(event) => setMember({ ...member, name: event.target.value })} data-testid="new-member-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-member-email">Email</Label>
                <Input id="new-member-email" type="email" required value={member.email} onChange={(event) => setMember({ ...member, email: event.target.value })} data-testid="new-member-email" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-member-phone">Phone</Label>
                <Input id="new-member-phone" type="tel" value={member.phone} onChange={(event) => setMember({ ...member, phone: event.target.value })} data-testid="new-member-phone" />
              </div>
              {admin && (
                <div className="space-y-1">
                  <Label htmlFor="new-member-coach">Assigned coach</Label>
                  <Select value={member.assigned_coach_id} onValueChange={(assigned_coach_id) => setMember({ ...member, assigned_coach_id })}>
                    <SelectTrigger id="new-member-coach" data-testid="new-member-coach"><SelectValue placeholder="Choose coach" /></SelectTrigger>
                    <SelectContent>{(coaches || []).filter((item) => item.crm_active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name || item.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="new-member-package">Membership plan</Label>
                <Select value={member.package_id} onValueChange={(package_id) => setMember({ ...member, package_id })}>
                  <SelectTrigger id="new-member-package" data-testid="new-member-package"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No plan</SelectItem>
                    {(packages || []).filter((item) => item.active).map((item) => (
                      <SelectItem key={item.id} value={item.id}>{item.name} · {item.duration_weeks} weeks</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <Switch checked={sendInvite} onCheckedChange={setSendInvite} data-testid="new-member-send-invite" />
              Create sign-in account and email OTP
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <Switch checked={sendWelcome} onCheckedChange={setSendWelcome} data-testid="new-member-send-welcome" />
              Send separate welcome email
            </label>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowAdd(false)} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving || !member.name.trim() || !member.email.trim() || (admin && !member.assigned_coach_id)} data-testid="new-member-submit">
                <Plus size={14} className="mr-1" />{saving ? "Adding…" : "Add member"}
              </Button>
            </div>
          </form>
        </Section>
      )}
      <Tabs value={status} onValueChange={(v) => setF("status", v)}>
        <TabsList className="mb-4 h-auto flex-wrap justify-start gap-1 border border-[#1E293B] bg-[#111827]" data-testid="clients-status-tabs">
          <TabsTrigger value="all" data-testid="clients-status-tab-all">All statuses</TabsTrigger>
          <TabsTrigger value="active" data-testid="clients-status-tab-active">Active</TabsTrigger>
          <TabsTrigger value="ending" data-testid="clients-status-tab-ending">Ending in 7 days</TabsTrigger>
          <TabsTrigger value="expired" data-testid="clients-status-tab-expired">Expired</TabsTrigger>
          <TabsTrigger value="inactive" data-testid="clients-status-tab-inactive">Inactive</TabsTrigger>
        </TabsList>
      </Tabs>
      <Section testid="clients-table">
        {!clients ? <div className="eyebrow">Loading members…</div> : !list.length ? <Empty text="No members match. Members are added in your CRM." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="eyebrow text-left"><th className="py-2">Member</th><th>Status</th><th>Plan</th><th>Start</th><th>Ends</th><th>Coach</th><th>Login</th></tr></thead>
              <tbody>
                {list.map((c) => {
                  const [label, cls] = LOGIN[c.login];
                  const ending = c.status === "active" && c.end_date && c.end_date <= in7();
                  return (
                    <tr key={c.id} className="border-t border-[#1E293B] row-hover" data-testid={`client-row-${c.id}`}>
                      <td className="py-3">
                        <Link to={`/coach/clients/${c.id}`} className="font-medium hover:text-[#FF3B30]" data-testid={`client-link-${c.id}`}>{c.name}</Link>
                        <div className="text-xs text-slate-500">{c.email}</div>
                      </td>
                      <td><StatusBadge status={c.status} testid={`client-status-${c.id}`} />{c.pending_checkins > 0 && <div className="text-[10px] text-amber-400 mt-1">{c.pending_checkins} check-in(s)</div>}</td>
                      <td className="text-slate-400">{c.package_name || "-"}</td>
                      <td className="text-slate-400 text-xs">{fmtDate(c.start_date)}</td>
                      <td className={`text-xs font-medium ${c.status === "expired" ? "text-red-400" : ending ? "text-amber-400" : "text-slate-300"}`}>{fmtDate(c.end_date)}</td>
                      <td className="text-slate-400 text-xs">{c.coach_name || "Unassigned"}</td>
                      <td className={`text-xs ${cls}`}>{label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
